import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { Feed } from "./partials/Feed/Feed";

import { endpoints } from "../../api/endpoints";
import { Meeting } from "../../models/Meeting";
import { Banner } from "../../components/Banner";
import { GuestHomeConversionBanner } from "../../components/GuestHomeConversionBanner/GuestHomeConversionBanner";
import { AdminBanner } from "../Admin/AdminDashboard/AdminBanner/AdminBanner";
import { HomeMeetingCard } from "../../components/HomeMeetingCard/HomeMeetingCard";
import { SimilarMentorCard } from "../../components/SimilarMentorCard/SimilarMentorCard";
import { User } from "../../models/Person";
import { routes } from "../../routes/routes";
import { RootState } from "../../store/store";
import { buildMeetingGridSlots } from "../../utils/meetingGrid";
import { filterDirectoryProfiles } from "../../utils/filterDirectoryProfiles";
import { useEventIntakeEnabled } from "../../hooks/useEventIntakeEnabled";

import "./DashBoard.css";

export const DashboardPreview = () => {
  const { t } = useTranslation();
  const eventIntakeEnabled = useEventIntakeEnabled();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const location = useLocation();

  const user = useSelector((state: RootState) => state.user.user);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [profiles, setProfiles] = useState<User[]>([]);
  const navigate = useNavigate();

  const getMeetings = async () => {
    const meetingsUrl = user?.isAdmin
      ? `${endpoints.admin.getMeetings}?timezone=${encodeURIComponent(timezone)}`
      : `${endpoints.meetings}?timezone=${encodeURIComponent(timezone)}`;

    axios
      .get(meetingsUrl)
      .then((response) => {
        setMeetings(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    if (!user?.id) return;
    getMeetings();
  }, [timezone, user?.id, user?.isAdmin]);

  useEffect(() => {
    axios
      .get(endpoints.getRandomMentors)
      .then((response) => {
        setProfiles(filterDirectoryProfiles(response.data.mentors));
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    if (location.hash === "#feed") {
      const feedElement = document.getElementById("feed");
      if (feedElement) {
        feedElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location]);

  const meetingGridSlots = buildMeetingGridSlots(meetings);

  return (
    <div className="dashboard-page">
      <div className="dashboard__inner dashboard__inner--guest-banner">
        <GuestHomeConversionBanner />
      </div>

      <div className="dashboard__hero">
        {user?.isAdmin ? <AdminBanner variant="hero" /> : <Banner variant="hero" />}
      </div>

      <div className="dashboard__inner">
        <section className="dashboard__browse" aria-labelledby="dashboard-browse-heading">
          <div className="dashboard__section-header">
            <h2 id="dashboard-browse-heading" className="dashboard__section-title">
              {eventIntakeEnabled
                ? t("home_page.browse_people")
                : t("home_page.browse_mentors")}
            </h2>
            <button
              type="button"
              className="dashboard__browse-all"
              onClick={() => navigate(routes.SEARCH)}
            >
              {t("home_page.browse_all")}
            </button>
          </div>

          <div className="dashboard__mentors-grid">
            {profiles.map((profile) => (
              <SimilarMentorCard key={profile.id} mentor={profile} />
            ))}
          </div>
        </section>

        <div className="dashboard__columns">
          <div id="feed" className="dashboard__feed-panel feed-panel">
            <Feed user={user} titleKey="home_page.community_feed" />
          </div>

          <aside className="dashboard__meetings-panel">
              <div className="dashboard__meetings-header">
                <h2 className="dashboard__meetings-title">{t("home_page.upcoming_meetings")}</h2>
                <p className="dashboard__meetings-disclaimer">{t("home_page.meeting_disclaimer")}</p>
              </div>

              {meetings.length > 0 ? (
                <>
                  <div className="dashboard__meetings-body">
                    <div className="dashboard__meetings-scroll" tabIndex={0} aria-label={t("home_page.upcoming_meetings")}>
                      <div
                        className={`dashboard__meetings-list${
                          meetings.length > 4 ? " dashboard__meetings-list--scrollable" : ""
                        }`}
                      >
                        {meetingGridSlots.map((meeting, index) =>
                          meeting ? (
                            <div key={meeting.id} className="home-meeting-card-wrap">
                              <HomeMeetingCard
                                meeting={meeting}
                                onCancelMeeting={getMeetings}
                              />
                            </div>
                          ) : (
                            <div
                              key={`meeting-slot-empty-${index}`}
                              className="home-meeting-card-wrap home-meeting-card-wrap--empty"
                              aria-hidden="true"
                            />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="dashboard__meetings-timezone">
                    {t("home_page.meetings_info")} ({timezone})
                  </p>
                </>
              ) : (
                <div className="dashboard__meetings-body">
                  <p className="dashboard__meetings-empty">{t("home_page.no_meetings")}</p>
                  <button
                    type="button"
                    className="dashboard__meetings-search-btn"
                    onClick={() => navigate(routes.SEARCH)}
                  >
                    {t("home_page.search_meetings_button")}
                  </button>
                </div>
              )}
          </aside>
        </div>
      </div>
    </div>
  );
};
